import { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { 
  PhoneIcon, 
  CalculatorIcon, 
  ClockIcon, 
  HomeIcon, 
  Bars3Icon, 
  XMarkIcon,
  BanknotesIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline'
import { useState } from 'react'
import classNames from 'classnames'
import logo from '../../assets/A&H Logo white.png'
import { PWAInstallPrompt } from '../PWAInstallPrompt'
import { useEffect } from 'react'

interface LayoutProps {
  children: ReactNode
}

const navigation = [
  { name: 'Home', href: '/', icon: HomeIcon },
  { name: 'Closing Costs', href: '/closing-costs', icon: CalculatorIcon },
  { name: 'Purchasing Power', href: '/dti-calculator', icon: BanknotesIcon },
  { name: 'Mortgage', href: '/mortgage-calculator', icon: CurrencyDollarIcon },
  { name: 'Closing Timeline', href: '/timeline-calculator', icon: ClockIcon },
]

export default function Layout({ children }: LayoutProps) {
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Function to handle smooth scrolling
  const handleNavigation = () => {
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  useEffect(() => {
    handleNavigation();
  }, [location.pathname]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen)
  }

  return (
    <div className="min-h-screen w-screen overflow-x-hidden">
      <PWAInstallPrompt />
      {/* Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 w-full bg-white shadow-md">
        <nav className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8 bg-white">
          <div className="flex items-center justify-between h-16 w-full">
            {/* Logo */}
            <div className="flex-shrink-0">
              <Link 
                to="/"
                onClick={handleNavigation}
              >
                <img
                  className="h-8 w-auto sm:h-10"
                  src={logo}
                  alt="A&H Legal"
                />
              </Link>
            </div>

            {/* Navigation Links - Desktop */}
            <div className="hidden md:flex flex-1 justify-center">
              <div className="flex items-center">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={handleNavigation}
                    className={classNames(
                      item.href === location.pathname
                        ? 'border-brand-600 text-brand-700'
                        : 'border-transparent text-gray-500 hover:text-brand-700 hover:border-brand-300',
                      'inline-flex items-center h-16 px-fluid-1 border-b-2 text-fluid-sm font-medium transition-all duration-300'
                    )}
                  >
                    <item.icon 
                      className={classNames(
                        'w-fluid-icon h-fluid-icon',
                        item.href === location.pathname ? 'text-brand-600' : 'text-gray-400 group-hover:text-brand-500'
                      )} 
                    />
                    <span className="ml-fluid-1 whitespace-nowrap">{item.name}</span>
                  </Link>
                ))}
              </div>
            </div>

            {/* CTA Button */}
            <div className="hidden sm:flex items-center flex-shrink-0">
              <a
                href="tel:847-318-3770"
                className="inline-flex items-center px-4 py-2 lg:px-6 lg:py-3 border border-transparent text-sm lg:text-base font-medium rounded-md text-white bg-gradient-to-r from-brand-600 to-brand-700 hover:from-brand-700 hover:to-brand-800 hover:text-white transform hover:scale-105 active:scale-95 transition-all duration-200 shadow-md hover:shadow-lg whitespace-nowrap"
              >
                <PhoneIcon className="h-5 w-5 mr-2" />
                847-318-3770
              </a>
            </div>

            {/* Mobile menu button */}
            <div className="flex items-center md:hidden">
              <button 
                type="button" 
                onClick={toggleMobileMenu}
                className="inline-flex items-center justify-center p-2 rounded-md bg-brand-600 hover:bg-brand-700 text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-500"
              >
                <span className="sr-only">Open main menu</span>
                {mobileMenuOpen ? (
                  <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                ) : (
                  <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile menu panel */}
          {mobileMenuOpen && (
            <div className="md:hidden absolute left-0 right-0 bg-white shadow-lg">
              <div className="pt-2 pb-3 space-y-1">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => {
                      setMobileMenuOpen(false);
                      handleNavigation();
                    }}
                    className={classNames(
                      item.href === location.pathname
                        ? 'bg-brand-50 border-brand-600 text-brand-700'
                        : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700',
                      'block pl-3 pr-4 py-2 border-l-4 text-sm font-medium transition-colors duration-200'
                    )}
                  >
                    <div className="flex items-center">
                      <item.icon className="h-4 w-4 mr-2" />
                      {item.name}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Hero section with gradient background */}
      <div className="w-screen bg-gradient-to-br from-brand-600 to-brand-800 pt-16"> 
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-white text-center">
          <h1 className="text-3xl sm:text-4xl font-display font-bold">
            Illinois Home Buyer Calculators
          </h1>
          <p className="mt-4 text-lg text-brand-100 max-w-2xl mx-auto">
            Professional tools to help you make informed decisions about your Illinois home purchase.
            Get accurate estimates and expert guidance throughout your journey.
          </p>
        </div>
      </div>

      {/* Main content */}
      <main className="w-screen pt-20">{/* Adds padding to prevent content from being hidden */}
        {children}
      </main>

      {/* Footer */}
      <footer className="w-screen bg-white mt-8">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="max-w-3xl mx-auto">
            <div className="bg-gradient-to-br from-brand-600 to-brand-800 rounded-lg text-white p-8 mb-8 shadow-lg text-center mt-4">
              <h2 className="text-2xl font-bold mb-4">Ready to Start Your Home Buying Journey?</h2>
              <p className="text-lg mb-6 text-brand-50">
                Get expert legal guidance throughout your Illinois home purchase.
                Schedule a free consultation with our experienced real estate attorneys.
              </p>
              <p className="text-xl font-semibold mb-4 text-white">
                Call Now for Free Consultation
              </p>
              <a 
                href="tel:847-318-3770" 
                className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-md text-brand-700 bg-white hover:bg-brand-50 transform hover:scale-105 active:scale-95 transition-all duration-200 shadow-md hover:shadow-lg hover:text-brand-700"
              >
                <PhoneIcon className="h-6 w-6 mr-2" />
                847-318-3770
              </a>
            </div>
          </div>
          <div className="text-center text-sm text-gray-600 pb-20 sm:pb-0">
            &copy; 2024 Designed by <a href="https://aruntastic.netlify.app/" target="_blank" rel="noopener noreferrer" className="now-bold-gradient-text hover:underline">aruntastic</a> for <a href="https://northsidelegal.com" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">Adler and Herbach</a>
          </div>
        </div>
      </footer>

      {/* Mobile bottom nav */}
      <div className="xs:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 pb-safe z-50">
        <a
          href="tel:847-318-3770"
          className="block w-full py-4 text-center text-white bg-gradient-to-r from-brand-600 to-brand-700 active:from-brand-700 active:to-brand-800 transition-all duration-200 hover:text-white"
        >
          <PhoneIcon className="h-4 w-4 inline-block mr-1.5" />
          <span className="text-sm whitespace-nowrap">Call Now: 847-318-3770</span>
        </a>
      </div>
    </div>
  )
}
